// src/pages/PlayersPage.jsx
// Player profiles, season stats, ownership %, points trend.
import React, { useMemo, useState } from "react";
import { IPL_TEAMS } from "../players";

const ROLE_ORDER = ["WK","BAT","ALR","BOWL"];
const ROLE_FILTER_OPTIONS = ["ALL","WK","BAT","ALR","BOWL"];

function TrendSparkline({ values = [], height = 32, width = 80 }) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const lastVal = values[values.length - 1];
  const color = lastVal >= 50 ? "#f5a623" : lastVal >= 25 ? "#4ade80" : "#8892a4";
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div style={{ textAlign: "center", minWidth: 44 }}>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 16, fontWeight: 700, color: color || "var(--text)" }}>
        {value ?? "—"}
      </div>
      <div style={{ fontSize: 9, color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 1 }}>
        {label}
      </div>
    </div>
  );
}

function PlayerModal({ player, stats, onClose }) {
  if (!player) return null;
  const teamMeta = IPL_TEAMS[player.team] || {};

  // Weekly points trend
  const weeklyPts = Object.entries(stats.playerPoints || {})
    .map(([wk, pts]) => ({ week: Number(wk), pts: pts[String(player.id)] ?? null }))
    .filter(x => x.pts !== null)
    .sort((a, b) => a.week - b.week);

  const totalPts   = weeklyPts.reduce((s, x) => s + x.pts, 0);
  const avgPts     = weeklyPts.length ? (totalPts / weeklyPts.length).toFixed(1) : "—";
  const bestPts    = weeklyPts.length ? Math.max(...weeklyPts.map(x => x.pts)) : "—";
  const lastPts    = weeklyPts.length ? weeklyPts[weeklyPts.length - 1].pts : "—";
  const trend      = weeklyPts.map(x => x.pts);

  // Season batting/bowling aggregates
  const weekStats = stats.playerStats || {};
  let totRuns = 0, totWkts = 0, totBalls = 0, totOvers = 0, totFours = 0, totSixes = 0;
  let totBowlRuns = 0, totCatches = 0;
  for (const wkPts of Object.values(weekStats)) {
    const s = wkPts[String(player.id)];
    if (!s) continue;
    totRuns     += Number(s.bat?.runs    || 0);
    totBalls    += Number(s.bat?.balls   || 0);
    totFours    += Number(s.bat?.fours   || 0);
    totSixes    += Number(s.bat?.sixes   || 0);
    totWkts     += Number(s.bowl?.wkts   || 0);
    totOvers    += Number(s.bowl?.overs  || 0);
    totBowlRuns += Number(s.bowl?.runs   || 0);
    totCatches  += Number(s.fld?.catches || 0) + Number(s.fld?.stumpings || 0);
  }
  const batAvg  = totBalls > 0 ? ((totRuns / totBalls) * 100).toFixed(0) : "—";
  const econ    = totOvers > 0 ? (totBowlRuns / totOvers).toFixed(2) : "—";
  const wkGames = weeklyPts.length;

  // Ownership: which teams have this player
  const ownerTeams = [];
  for (const team of (stats.allTeams || [])) {
    if ((team.squadIds || []).map(String).includes(String(player.id))) {
      ownerTeams.push(team.name);
    }
  }
  const ownerPct = stats.totalTeams
    ? Math.round((ownerTeams.length / stats.totalTeams) * 100)
    : ownerTeams.length > 0 ? 100 : 0;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: "var(--bg3)", border: "1px solid var(--border2)",
        borderRadius: "var(--radius-lg)", width: "100%", maxWidth: 520,
        maxHeight: "90vh", overflowY: "auto",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--border)",
          background: `linear-gradient(135deg, ${teamMeta.color}18 0%, transparent 100%)`,
          borderTop: `3px solid ${teamMeta.color || "var(--border)"}`,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 900 }}>
                {player.name}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <span className={`role-chip role-${player.role}`}>{player.role}</span>
                <span className="team-chip" style={{ color: teamMeta.color }}>{player.team}</span>
                <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{teamMeta.name}</span>
              </div>
            </div>
            <button className="mini" onClick={onClose} style={{ fontSize: 16, lineHeight: 1 }}>✕</button>
          </div>
        </div>

        {/* Fantasy points */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
            color: "var(--text-muted)", marginBottom: 10 }}>Fantasy Points</div>
          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <StatBadge label="Total"   value={totalPts || "—"} color="var(--gold)" />
            <StatBadge label="Avg/Wk"  value={avgPts}          color="var(--green)" />
            <StatBadge label="Best"    value={bestPts}          color="var(--blue)" />
            <StatBadge label="Last"    value={lastPts} />
            <StatBadge label="Weeks"   value={wkGames} />
            <div style={{ marginLeft: "auto" }}>
              <TrendSparkline values={trend} />
              {trend.length > 1 && (
                <div style={{ fontSize: 9, color: "var(--text-muted)", textAlign: "center", marginTop: 2 }}>
                  {trend.length} wk trend
                </div>
              )}
            </div>
          </div>

          {/* Weekly breakdown */}
          {weeklyPts.length > 0 && (
            <div style={{ marginTop: 12, display: "flex", gap: 4, flexWrap: "wrap" }}>
              {weeklyPts.map(({ week, pts }) => (
                <div key={week} style={{
                  minWidth: 36, padding: "4px 6px", borderRadius: 4, textAlign: "center",
                  background: pts >= 60 ? "rgba(245,166,35,0.15)" : pts >= 30 ? "rgba(74,222,128,0.1)" : "var(--bg2)",
                  border: `1px solid ${pts >= 60 ? "rgba(245,166,35,0.3)" : pts >= 30 ? "rgba(74,222,128,0.2)" : "var(--border)"}`,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700,
                    color: pts >= 60 ? "var(--gold)" : pts >= 30 ? "var(--green)" : "var(--text)" }}>
                    {pts}
                  </div>
                  <div style={{ fontSize: 8, color: "var(--text-muted)" }}>W{week}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cricket stats */}
        {(totRuns > 0 || totWkts > 0 || totCatches > 0) && (
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
              color: "var(--text-muted)", marginBottom: 10 }}>Season Cricket Stats</div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              {totRuns > 0 && <StatBadge label="Runs" value={totRuns} color="var(--blue)" />}
              {totBalls > 0 && <StatBadge label="SR" value={batAvg} />}
              {totFours > 0 && <StatBadge label="4s" value={totFours} />}
              {totSixes > 0 && <StatBadge label="6s" value={totSixes} color="var(--gold)" />}
              {totWkts > 0 && <StatBadge label="Wkts" value={totWkts} color="var(--green)" />}
              {totOvers > 0 && <StatBadge label="Econ" value={econ} />}
              {totCatches > 0 && <StatBadge label="Ct/St" value={totCatches} />}
            </div>
          </div>
        )}

        {/* Ownership */}
        <div style={{ padding: "14px 20px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
            color: "var(--text-muted)", marginBottom: 8 }}>Ownership</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700,
              color: ownerPct > 50 ? "var(--gold)" : "var(--text)" }}>
              {ownerPct}%
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ background: "var(--bg2)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${ownerPct}%`, borderRadius: 4,
                  background: ownerPct > 50 ? "var(--gold)" : "var(--green)",
                  transition: "width 0.3s ease" }} />
              </div>
              {ownerTeams.length > 0 && (
                <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>
                  Owned by: {ownerTeams.join(", ")}
                </div>
              )}
              {ownerTeams.length === 0 && (
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                  Free agent — not rostered
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlayersPage({ league, room }) {
  const [search,     setSearch]     = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [teamFilter, setTeamFilter] = useState("ALL");
  const [sortBy,     setSortBy]     = useState("pts"); // pts | name | team | role
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const playerMap  = useMemo(() => room?.players || {}, [room]);
  const allTeams   = useMemo(() => room?.teams   || [], [room]);
  const totalTeams = allTeams.length;

  // Build roster lookup: playerId → fantasy team name
  const rosterMap = useMemo(() => {
    const m = {};
    allTeams.forEach(t => (t.squadIds || []).map(String).forEach(id => { m[id] = t.name; }));
    return m;
  }, [allTeams]);

  // Season player points totals
  const playerPointTotals = useMemo(() => {
    const totals = {};
    const pp = league?.season?.playerPoints || {};
    for (const weekPts of Object.values(pp)) {
      for (const [pid, pts] of Object.entries(weekPts)) {
        totals[pid] = (totals[pid] || 0) + pts;
      }
    }
    return totals;
  }, [league]);

  // Last week points
  const lastWeekPts = useMemo(() => {
    const pp = league?.season?.playerPoints || {};
    const weeks = Object.keys(pp).map(Number).sort((a,b) => b - a);
    return weeks.length ? pp[String(weeks[0])] || {} : {};
  }, [league]);

  // All players from room
  const allPlayers = useMemo(() => Object.values(playerMap), [playerMap]);

  // IPL teams list for filter
  const iplTeams = useMemo(() => {
    const teams = new Set(allPlayers.map(p => p.team).filter(Boolean));
    return ["ALL", ...Array.from(teams).sort()];
  }, [allPlayers]);

  // Filter + sort
  const displayPlayers = useMemo(() => {
    let list = allPlayers.filter(p => {
      const matchRole   = roleFilter === "ALL" || p.role === roleFilter;
      const matchTeam   = teamFilter === "ALL" || p.team === teamFilter;
      const matchSearch = !search.trim() || p.name.toLowerCase().includes(search.toLowerCase());
      return matchRole && matchTeam && matchSearch;
    });
    list.sort((a, b) => {
      if (sortBy === "pts")  return (playerPointTotals[String(b.id)] || 0) - (playerPointTotals[String(a.id)] || 0);
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "team") return (a.team || "").localeCompare(b.team || "");
      if (sortBy === "role") return ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
      return 0;
    });
    return list;
  }, [allPlayers, roleFilter, teamFilter, search, sortBy, playerPointTotals]);

  const statsContext = {
    playerPoints: league?.season?.playerPoints || {},
    playerStats:  league?.season?.stats        || {},
    allTeams,
    totalTeams,
  };

  if (allPlayers.length === 0) {
    return (
      <div className="panel" style={{ padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>👤</div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Player Profiles</div>
        <div style={{ color: "var(--text-dim)", fontSize: 13 }}>
          Player data will appear here once the draft is complete.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="panel" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input className="search-box" placeholder="Search player…" value={search}
            onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 160 }} />

          <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)}>
            {iplTeams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <div style={{ display: "flex", gap: 4 }}>
            {ROLE_FILTER_OPTIONS.map(r => (
              <button key={r} className={roleFilter === r ? "primary" : "mini"}
                onClick={() => setRoleFilter(r)} style={{ fontSize: 11, fontWeight: 700 }}>
                {r}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            <span className="label" style={{ alignSelf: "center" }}>Sort:</span>
            {[["pts","Points"],["name","Name"],["team","IPL Team"],["role","Role"]].map(([v,l]) => (
              <button key={v} className={sortBy === v ? "primary" : "mini"}
                onClick={() => setSortBy(v)} style={{ fontSize: 11 }}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Column headers */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 80px 56px 56px 56px 80px",
        gap: 8, padding: "6px 14px",
        fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
        color: "var(--text-muted)", textTransform: "uppercase",
        borderBottom: "2px solid var(--border2)",
      }}>
        <span>Player</span>
        <span style={{ textAlign: "right" }}>Total Pts</span>
        <span style={{ textAlign: "right" }}>Last Wk</span>
        <span style={{ textAlign: "right" }}>Avg/Wk</span>
        <span style={{ textAlign: "right" }}>Own%</span>
        <span style={{ textAlign: "right" }}>Trend</span>
      </div>

      <div className="panel">
        {displayPlayers.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text-dim)" }}>No players match filters.</div>
        ) : (
          displayPlayers.map(player => {
            const pid        = String(player.id);
            const totalPts   = playerPointTotals[pid] || 0;
            const lastPts    = lastWeekPts[pid] ?? null;
            const teamColor  = IPL_TEAMS[player.team]?.color || "var(--text-dim)";
            const fantasyTeam= rosterMap[pid];
            const weeklyArr  = Object.entries(league?.season?.playerPoints || {})
              .sort((a,b) => Number(a[0]) - Number(b[0]))
              .map(([, pts]) => pts[pid] ?? 0);
            const avgPts = weeklyArr.length ? (totalPts / weeklyArr.filter(x=>x>0).length || 0).toFixed(1) : "—";
            const ownerTeamsCount = allTeams.filter(t => (t.squadIds||[]).map(String).includes(pid)).length;
            const ownerPct = totalTeams ? Math.round((ownerTeamsCount / totalTeams) * 100) : 0;

            return (
              <div key={player.id}
                onClick={() => setSelectedPlayer(player)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 80px 56px 56px 56px 80px",
                  gap: 8, padding: "9px 14px",
                  borderBottom: "1px solid var(--border)",
                  cursor: "pointer", alignItems: "center",
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.025)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {/* Player info */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {player.name}
                  </div>
                  <div style={{ display: "flex", gap: 5, marginTop: 3, alignItems: "center" }}>
                    <span className={`role-chip role-${player.role}`}>{player.role}</span>
                    <span className="team-chip" style={{ color: teamColor }}>{player.team}</span>
                    {fantasyTeam
                      ? <span style={{ fontSize: 10, color: "var(--text-dim)" }}>→ {fantasyTeam}</span>
                      : <span style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic" }}>Free agent</span>
                    }
                  </div>
                </div>

                {/* Total pts */}
                <div style={{ textAlign: "right", fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 18, fontWeight: 700,
                  color: totalPts >= 200 ? "var(--gold)" : totalPts >= 100 ? "var(--green)" : "var(--text)" }}>
                  {totalPts || "—"}
                </div>

                {/* Last week */}
                <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600,
                  color: lastPts === null ? "var(--text-muted)" : lastPts >= 60 ? "var(--gold)" : lastPts >= 30 ? "var(--green)" : "var(--text)" }}>
                  {lastPts ?? "—"}
                </div>

                {/* Avg */}
                <div style={{ textAlign: "right", fontSize: 12, color: "var(--text-dim)" }}>{avgPts}</div>

                {/* Own% */}
                <div style={{ textAlign: "right", fontSize: 12,
                  color: ownerPct > 50 ? "var(--gold)" : "var(--text-dim)" }}>
                  {ownerPct}%
                </div>

                {/* Trend */}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <TrendSparkline values={weeklyArr.slice(-6)} width={64} height={24} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Player modal */}
      {selectedPlayer && (
        <PlayerModal
          player={selectedPlayer}
          stats={statsContext}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </div>
  );
}
